import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { iconPlusButton, iconSearchButton } from '../../assets';
import { useTheme } from '../../theme';

export interface FilterOption {
  value: string;
  label: string;
}

export interface CoursesFilterProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  categoryOptions: FilterOption[];
  categoryValue: string;
  onCategoryChange: (value: string) => void;
  categoryLabel?: string;
  statusOptions: FilterOption[];
  statusValue: string;
  onStatusChange: (value: string) => void;
  statusLabel?: string;
  dateValue: string;
  onDateChange: (value: string) => void;
  dateLabel?: string;
  onCreateCourse?: () => void;
  createButtonLabel?: string;
  createAriaLabel?: string;
  /** Identificador de teste para o input de busca */
  dataE2eSearchInput?: string;
  /** Identificador de teste para o botão de criar curso */
  dataE2eCreateButton?: string;
}

function ChevronDown() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <path d="M7 10l5 5 5-5z" />
    </svg>
  );
}

export function CoursesFilter({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  categoryOptions,
  categoryValue,
  onCategoryChange,
  categoryLabel,
  statusOptions,
  statusValue,
  onStatusChange,
  statusLabel,
  dateValue,
  onDateChange,
  dateLabel,
  onCreateCourse,
  createButtonLabel,
  createAriaLabel,
  dataE2eSearchInput,
  dataE2eCreateButton,
}: CoursesFilterProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  };

  const boxStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'stretch',
    flex: '1 1 280px',
    minWidth: 200,
    maxWidth: 720,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.filterBox.border}`,
    borderRadius: theme.shape.borderRadius.md,
    backgroundColor: theme.palette.filterBox.background,
    overflow: 'visible',
  };

  const searchWrapperStyle: React.CSSProperties = {
    position: 'relative',
    flex: 1,
    minWidth: 90,
    margin: theme.spacing.sm,
    backgroundColor: theme.palette.searchInFilter.background,
    borderRadius: theme.shape.borderRadius.md,
    overflow: 'hidden',
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    minHeight: 40,
    padding: `${theme.spacing.inputY} ${theme.spacing.md} ${theme.spacing.inputY} 2.5rem`,
    fontSize: theme.typography.fontSize.base,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.searchInFilter.background,
    border: 'none',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const iconSpanStyle: React.CSSProperties = {
    position: 'absolute',
    left: theme.spacing.md,
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.searchInFilter.icon,
    pointerEvents: 'none',
  };

  const filterButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    margin: theme.spacing.sm,
    width: 140,
    minWidth: 140,
    height: 40,
    borderRadius: theme.shape.borderRadius.sm,
    backgroundColor: theme.palette.filterBox.background,
    border: `${theme.shape.borderWidth.thin} solid ${theme.palette.button.filterBorder}`,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.base,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.palette.button.filterText,
    cursor: 'pointer',
    boxSizing: 'border-box',
  };

  const filterButtonLabelStyle: React.CSSProperties = {
    flex: '1 1 0',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const actionButtonStyle: React.CSSProperties = onCreateCourse
    ? {
        fontSize: theme.typography.fontSize.base,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.palette.primary.contrastText,
        backgroundColor: theme.palette.primary.main,
        borderRadius: theme.shape.borderRadius.sm2,
        border: 'none',
        cursor: 'pointer',
        minWidth: 180,
        width: 180,
        height: 42,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing.sm,
        marginLeft: theme.spacing.lg,
      }
    : {};

  const dropdownListStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 'calc(100% + 4px)',
    minWidth: '100%',
    padding: theme.spacing.xs,
    margin: 0,
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.sm,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    listStyle: 'none',
    zIndex: 11,
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    zIndex: 10,
  };

  const searchPlaceholderText = searchPlaceholder ?? t('courses.search_placeholder');
  const searchAriaText = searchAriaLabel ?? t('courses.search_aria');
  const categoryLabelText = categoryLabel ?? t('courses.category_label');
  const statusLabelText = statusLabel ?? t('courses.status_label');
  const dateLabelText = dateLabel ?? t('courses.date_label');
  const createLabelText = createButtonLabel ?? t('courses.create_button');
  const createAriaText = createAriaLabel ?? t('courses.create_aria');

  const getCategoryDisplayLabel = () => {
    const opt = categoryOptions.find(o => o.value === categoryValue);
    return opt ? opt.label : categoryLabelText;
  };

  const getStatusDisplayLabel = () => {
    const opt = statusOptions.find(o => o.value === statusValue);
    return opt ? opt.label : statusLabelText;
  };

  const getDateDisplayLabel = () => {
    if (!dateValue) return dateLabelText;
    try {
      const d = new Date(dateValue);
      return Number.isNaN(d.getTime()) ? dateLabelText : d.toLocaleDateString();
    } catch {
      return dateLabelText;
    }
  };

  const searchInputClassName = 'courses-search-input';
  const btnClassName = 'courses-btn';

  const renderDropdownOption = (
    opt: FilterOption,
    selected: boolean,
    onSelect: () => void
  ) => (
    <button
      type="button"
      onClick={onSelect}
      style={{
        display: 'block',
        width: '100%',
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        textAlign: 'left',
        backgroundColor: selected ? theme.palette.primary.main : theme.palette.background.paper,
        color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
        border: 'none',
        borderRadius: theme.shape.borderRadius.sm,
        fontSize: theme.typography.fontSize.sm,
        fontFamily: theme.typography.fontFamily,
        cursor: 'pointer',
      }}
      onMouseEnter={e => {
        if (!selected) e.currentTarget.style.backgroundColor = theme.palette.background.default;
      }}
      onMouseLeave={e => {
        if (!selected) e.currentTarget.style.backgroundColor = theme.palette.background.paper;
      }}
    >
      {opt.label}
    </button>
  );

  return (
    <div style={containerStyle} role="search">
      <style>{`.${searchInputClassName}::placeholder { color: ${theme.palette.searchInFilter.placeholder}; }`}</style>

      <div style={boxStyle}>
        <div style={searchWrapperStyle}>
          <span style={iconSpanStyle} aria-hidden>
            <img src={iconSearchButton} alt="" width={16} height={16} />
          </span>
          <input
            type="text"
            value={searchValue}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={searchPlaceholderText}
            style={searchInputStyle}
            className={searchInputClassName}
            aria-label={searchAriaText}
            data-e2e={dataE2eSearchInput}
          />
        </div>

        {/* All Categories */}
        <div style={{ position: 'relative', zIndex: categoryOpen ? 12 : undefined }}>
          <button
            type="button"
            style={filterButtonStyle}
            onClick={() => {
              setCategoryOpen(o => !o);
              setStatusOpen(false);
            }}
            aria-expanded={categoryOpen}
            aria-haspopup="listbox"
            aria-label={categoryLabelText}
          >
            <span style={filterButtonLabelStyle}>{getCategoryDisplayLabel()}</span>
            <ChevronDown />
          </button>
          {categoryOpen && (
            <>
              <div role="presentation" style={overlayStyle} onClick={() => setCategoryOpen(false)} />
              <ul role="listbox" aria-label={categoryLabelText} style={dropdownListStyle}>
                {categoryOptions.map(opt => (
                  <li key={opt.value} role="option" aria-selected={opt.value === categoryValue} style={{ marginBottom: '2px' }}>
                    {renderDropdownOption(opt, opt.value === categoryValue, () => {
                      onCategoryChange(opt.value);
                      setCategoryOpen(false);
                    })}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* All Status */}
        <div style={{ position: 'relative', zIndex: statusOpen ? 12 : undefined }}>
          <button
            type="button"
            style={filterButtonStyle}
            onClick={() => {
              setStatusOpen(o => !o);
              setCategoryOpen(false);
            }}
            aria-expanded={statusOpen}
            aria-haspopup="listbox"
            aria-label={statusLabelText}
          >
            <span style={filterButtonLabelStyle}>{getStatusDisplayLabel()}</span>
            <ChevronDown />
          </button>
          {statusOpen && (
            <>
              <div role="presentation" style={overlayStyle} onClick={() => setStatusOpen(false)} />
              <ul role="listbox" aria-label={statusLabelText} style={dropdownListStyle}>
                {statusOptions.map(opt => (
                  <li key={opt.value} role="option" aria-selected={opt.value === statusValue} style={{ marginBottom: '2px' }}>
                    {renderDropdownOption(opt, opt.value === statusValue, () => {
                      onStatusChange(opt.value);
                      setStatusOpen(false);
                    })}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Date Created */}
        <div style={{ position: 'relative' }}>
          <input
            ref={dateInputRef}
            type="date"
            value={dateValue || ''}
            onChange={e => onDateChange(e.target.value)}
            aria-label={dateLabelText}
            style={{
              position: 'absolute',
              opacity: 0,
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
          />
          <button
            type="button"
            style={filterButtonStyle}
            onClick={() => {
              setCategoryOpen(false);
              setStatusOpen(false);
              dateInputRef.current?.showPicker?.();
            }}
            aria-haspopup="dialog"
            aria-label={dateLabelText}
          >
            <span style={filterButtonLabelStyle}>{getDateDisplayLabel()}</span>
            <ChevronDown />
          </button>
        </div>
      </div>

      {onCreateCourse && (
        <button
          type="button"
          className={btnClassName}
          onClick={onCreateCourse}
          style={actionButtonStyle}
          aria-label={createAriaText}
          data-e2e={dataE2eCreateButton}
        >
          <img src={iconPlusButton} alt="" width={16} height={10} />
          {createLabelText}
        </button>
      )}
    </div>
  );
}
